import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Loader2, ExternalLink, CalendarDays } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { bankColor } from "@/lib/bank";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";

const Ctx = createContext({ openQuickProfile: () => {} });
export const useQuickProfile = () => useContext(Ctx);

function waLink(no) {
  let d = (no || "").replace(/\D/g, "");
  if (!d) return null;
  if (d.startsWith("620")) d = "62" + d.slice(3);
  else if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (!d.startsWith("62")) d = "62" + d;
  if (d.length < 9) return null;
  return `https://wa.me/${d}`;
}

const Line = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 border-b border-black/[0.05] py-2 last:border-0">
    <span className="text-[12.5px] text-[#8E8E93]">{label}</span>
    <span className="text-right text-[13.5px] font-medium text-[#1D1D1F]">{children}</span>
  </div>
);

export function QuickProfileProvider({ children }) {
  const [empId, setEmpId] = useState(null);
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  const openQuickProfile = useCallback((id) => { if (id) setEmpId(id); }, []);
  const close = () => { setEmpId(null); setEmp(null); };

  useEffect(() => {
    if (!empId) return;
    setLoading(true); setEmp(null);
    api.get(`/employees/${empId}/profile`)
      .then((r) => setEmp(r.data))
      .catch((e) => { errMsg && errMsg(e); close(); })
      .finally(() => setLoading(false));
  }, [empId]);

  const wa = emp && waLink(emp.no_hp);

  return (
    <Ctx.Provider value={{ openQuickProfile }}>
      {children}
      <Dialog open={!!empId} onOpenChange={(v) => !v && close()}>
        <DialogContent data-testid="quick-profile" className="gap-0 rounded-[22px] border border-white/50 bg-white/90 p-0 backdrop-blur-2xl sm:max-w-[420px]">
          <DialogHeader className="sr-only"><DialogTitle>Employee</DialogTitle></DialogHeader>
          {loading || !emp ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="p-5">
              <div className="flex items-center gap-3.5">
                <EmployeeAvatar id={emp.id} nama={emp.nama} hasPhoto={emp.has_photo} size={56} />
                <div className="min-w-0">
                  <p data-testid="quick-profile-name" className="flex items-center gap-1.5 truncate text-[17px] font-semibold tracking-tight">
                    <span className="truncate">{emp.nama}</span>
                    {emp.verified && <VerifiedBadge size={18} className="shrink-0" title="Verified employee" />}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                    <span className="num font-medium text-[#0071E3]">{emp.emp_code || "—"}</span>
                    <span className="text-[#C7C7CC]">·</span>
                    <span className="text-[#6E6E73]">{emp.jabatan || "—"}</span>
                  </div>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${emp.aktif ? "bg-[#E9F9EF] text-[#0E8A46]" : "bg-[#F2F2F7] text-[#8E8E93]"}`}>{emp.aktif ? "Active" : "Inactive"}</span>
                </div>
              </div>

              <div className="mt-4">
                <Line label="Phone">{emp.no_hp || "—"}</Line>
                <Line label="Tanggal Lahir">{emp.tanggal_lahir ? (() => { const b = new Date(emp.tanggal_lahir + "T00:00:00"); const txt = b.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }); const n = new Date(); let a = n.getFullYear() - b.getFullYear(); const m = n.getMonth() - b.getMonth(); if (m < 0 || (m === 0 && n.getDate() < b.getDate())) a -= 1; return `${txt}${a >= 0 && a < 130 ? ` (${a} years old)` : ""}`; })() : "—"}</Line>
                <Line label="Tahun Bergabung">{emp.tahun_masuk || "—"}</Line>
                <Line label="Payroll Type">{emp.payroll_type === "PIECE_RATE" ? "Tukang Jarit" : "Harian"}</Line>
                <Line label="BPJS">{emp.bpjs ? "Yes" : "No"}</Line>
                {emp.metode === "transfer" && <>
                  <Line label="Bank"><span className="font-semibold" style={{ color: bankColor(emp.bank) }}>{(emp.bank || "—").toUpperCase()}</span></Line>
                  <Line label="Account No.">{emp.no_rekening || "—"}</Line>
                </>}
                {isAdmin && emp.payroll_type !== "PIECE_RATE" && <Line label="Rate / Hari">{rp(emp.rate_harian || 0)}</Line>}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {wa && (
                  <a data-testid="quick-profile-whatsapp" href={wa} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#25D366] px-3.5 text-[13px] font-semibold text-white active:scale-95"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                )}
                <button data-testid="quick-profile-full" onClick={() => { const id = emp.id; close(); navigate(`/employees/${id}`); }} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[13px] font-medium text-[#0071E3] active:scale-95"><ExternalLink className="h-4 w-4" /> View Full Profile</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}
